import {getTreatmentReport, IPatientTreatmentReport, IPatientTreatmentReportResponse} from 'core/api/patient'
import {IPayloadAction} from 'core/store'
import {Action, Reducer} from 'redux'
import {call, put, takeLatest} from 'redux-saga/effects'
import {actions as mainActions} from '../main'
export const SET_SUBJECT_TREATMENT_REPORT = 'subjects/SET_SUBJECT_TREATMENT_REPORT'

export interface ISubjectTreatmentReport {
  subjectIdentifier: string
  treatmentReport: IPatientTreatmentReport
}
type IActionSetSubjectTreatmentReportPayload = ISubjectTreatmentReport
export interface IActionSetSubjectTreatmentReport
  extends IPayloadAction<typeof SET_SUBJECT_TREATMENT_REPORT, IActionSetSubjectTreatmentReportPayload> {}

export const setSubjectTreatmentReport = (
  payload: IActionSetSubjectTreatmentReportPayload,
): IActionSetSubjectTreatmentReport => ({
  payload,
  type: SET_SUBJECT_TREATMENT_REPORT,
})

const SET_TREATMENT_REPORT_IS_FETCHING = 'subjects/SET_TREATMENT_REPORT_IS_FETCHING'

export interface IActionSetTreatmentReportIsFetching
  extends IPayloadAction<typeof SET_TREATMENT_REPORT_IS_FETCHING, boolean> {}

export const setTreatmentReportIsFetching = (): IActionSetTreatmentReportIsFetching => ({
  type: SET_TREATMENT_REPORT_IS_FETCHING,
  payload: true,
})

export const resetTreatmentReportIsFetching = (): IActionSetTreatmentReportIsFetching => ({
  type: SET_TREATMENT_REPORT_IS_FETCHING,
  payload: false,
})

export type TAction =
  IActionSetSubjectTreatmentReport
  | IActionSetTreatmentReportIsFetching

export interface IState {
  reports: ISubjectTreatmentReport[]
  isFetching: boolean
}

const initialState: IState = {
  reports: [],
  isFetching: false,
}

export const unwrappedReducer = (state: IState = initialState, action: TAction): IState => {
  switch (action.type) {
    case SET_SUBJECT_TREATMENT_REPORT:
      return {
        ...state,
        reports: state.reports.reduce((trs: ISubjectTreatmentReport[], tr) => {
          if (tr.subjectIdentifier === action.payload.subjectIdentifier) {
            return trs
          }
          return [...trs, tr]
        }, [action.payload]),
      }
    case SET_TREATMENT_REPORT_IS_FETCHING:
      return {
        ...state,
        isFetching: action.payload,
      }
    default:
      return state
  }
}

// TODO: Workaround for redux typings issue. Fixed in redux v4.
// https://github.com/reactjs/redux/issues/2709
export const reducer: Reducer<IState> = (state: IState, action: Action) => (
  unwrappedReducer(state, action as TAction)
)

const GET_SUBJECT_TREATMENT_REPORT_REQUEST = 'subjects/GET_SUBJECT_TREATMENT_REPORT_REQUEST'

interface IActionGetSubjectTreatmentReportRequestPayload {
  subjectIdentifier: string
}

interface IActionGetSubjectTreatmentReportRequest
  extends IPayloadAction<
    typeof GET_SUBJECT_TREATMENT_REPORT_REQUEST,
    IActionGetSubjectTreatmentReportRequestPayload
  > {}

export const getSubjectTreatmentReportRequest = (
  subjectIdentifier: string,
): IActionGetSubjectTreatmentReportRequest => ({
  type: GET_SUBJECT_TREATMENT_REPORT_REQUEST,
  payload: {subjectIdentifier},
})

export interface IDependencies {
  withAuth: (fn: (...args: any[]) => any) => void
  onTokenExpired: () => void
  subjectIdentifierToPatientId: (subjectIdentifier: string) => void
}

export function* getSubjectTreatmentReportRequestSaga(
  dependencies: IDependencies,
  action: IActionGetSubjectTreatmentReportRequest,
) {
  yield put(setTreatmentReportIsFetching())
  const subjectIdentifier = action.payload.subjectIdentifier
  const patientId: string | null = yield call(dependencies.subjectIdentifierToPatientId, subjectIdentifier)
  if (!patientId) {
    yield put(mainActions.snackbarSetRequest('Oops! Something went wrong. Please try again later.'))
    yield put(resetTreatmentReportIsFetching())
    return
  }

  let response: IPatientTreatmentReportResponse
  try {
    response = yield call(dependencies.withAuth, getTreatmentReport, patientId)
  } catch (e) {
    // TODO I18N
    yield put(mainActions.snackbarSetRequest('Network error, please check your connection and try again.'))
    yield put(resetTreatmentReportIsFetching())
    return
  }
  if (response.status === 200) {
    yield put(setSubjectTreatmentReport({
      subjectIdentifier,
      treatmentReport: response.data.treatmentReport,
    }))
  } else if (response.status === 401) {
    yield call(dependencies.onTokenExpired)
  } else {
    // TODO I18N
    yield put(mainActions.snackbarSetRequest('Oops! Something went wrong. Please try again later.'))
  }
  yield put(resetTreatmentReportIsFetching())
}

export function* saga(dependencies: IDependencies) {
  yield [
    takeLatest(GET_SUBJECT_TREATMENT_REPORT_REQUEST, getSubjectTreatmentReportRequestSaga, dependencies),
  ]
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./study_coordinator/src/store/subjects/treatmentReport.ts